import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ImageIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "../../../lib/utils";
import { automationTestIds } from "../../../testing/automationTestIds";
import { findCountryOption } from "../utils/countryOptions";

const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 424;

const PRIMARY = "var(--cf-primary-color)";
const GOLD = "#C9A44C";
const GOLD_SOFT = "#E7D3A1";
const INK = "var(--heading-text-color)";
const MUTED = "#98A2B3";
const SERIF = 'Georgia, "Times New Roman", Times, ui-serif, serif';

const useFlash = (value, duration = 650) => {
  const [flashing, setFlashing] = useState(false);
  const firstRef = useRef(true);

  useEffect(() => {
    if (firstRef.current) {
      firstRef.current = false;
      return undefined;
    }
    setFlashing(true);
    const timer = setTimeout(() => setFlashing(false), duration);
    return () => clearTimeout(timer);
  }, [value, duration]);

  return flashing;
};

const Live = ({ value, children, className = "", style }) => {
  const flashing = useFlash(value);
  return (
    <span
      className={cn(
        "inline-block rounded-[4px] px-1 transition-[background-color,box-shadow] duration-500",
        className,
      )}
      style={{
        backgroundColor: flashing
          ? "color-mix(in srgb, var(--cf-primary-color) 14%, transparent)"
          : "transparent",
        boxShadow: flashing
          ? "0 0 0 3px color-mix(in srgb, var(--cf-primary-color) 8%, transparent)"
          : "none",
        ...style,
      }}
    >
      {children}
    </span>
  );
};

const useCanvasScale = () => {
  const ref = useRef(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const measure = () => {
      const width = node.clientWidth;
      if (width > 0) setScale(Math.min(width / CANVAS_WIDTH, 1.25));
    };
    measure();

    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, scale];
};

const QR_MODULES = (() => {
  const size = 21;
  const cells = [];
  const inFinder = (x, y) =>
    (x < 7 && y < 7) || (x >= size - 7 && y < 7) || (x < 7 && y >= size - 7);
  let seed = 7;
  const next = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      if (!inFinder(x, y) && next() > 0.52) cells.push([x, y]);
    }
  }
  return { size, cells };
})();

const FakeQr = ({ size = 46 }) => {
  const { size: n, cells } = QR_MODULES;
  const finder = (x, y) => (
    <g key={`${x}-${y}`}>
      <rect x={x} y={y} width={7} height={7} fill={INK} />
      <rect x={x + 1} y={y + 1} width={5} height={5} fill="#fff" />
      <rect x={x + 2} y={y + 2} width={3} height={3} fill={INK} />
    </g>
  );
  return (
    <svg
      viewBox={`0 0 ${n} ${n}`}
      width={size}
      height={size}
      aria-hidden="true"
      shapeRendering="crispEdges"
    >
      <rect width={n} height={n} fill="#fff" />
      {cells.map(([x, y]) => (
        <rect key={`${x}:${y}`} x={x} y={y} width={1} height={1} fill={INK} />
      ))}
      {finder(0, 0)}
      {finder(n - 7, 0)}
      {finder(0, n - 7)}
    </svg>
  );
};

const Corner = ({ position }) => {
  const placement = {
    tl: { top: 22, left: 22, transform: "none" },
    tr: { top: 22, right: 22, transform: "scaleX(-1)" },
    bl: { bottom: 22, left: 22, transform: "scaleY(-1)" },
    br: { bottom: 22, right: 22, transform: "scale(-1,-1)" },
  }[position];
  return (
    <svg
      viewBox="0 0 28 28"
      width={28}
      height={28}
      aria-hidden="true"
      style={{ position: "absolute", ...placement }}
    >
      <path d="M1 27V1h26" fill="none" stroke={GOLD} strokeWidth="1.5" />
      <path d="M5 27V5h22" fill="none" stroke={GOLD_SOFT} strokeWidth="1" />
      <circle cx="5" cy="5" r="1.6" fill={GOLD} />
    </svg>
  );
};

const SEAL_RAYS = Array.from({ length: 24 }, (_, ray) => {
  const angle = (ray / 24) * Math.PI * 2;
  return {
    key: `ray-${ray}`,
    cx: 32 + Math.cos(angle) * 26,
    cy: 32 + Math.sin(angle) * 26,
  };
});

const Seal = () => {
  const fillId = `onboarding-seal-fill-${useId().replace(/:/g, "")}`;

  return (
    <svg viewBox="0 0 64 64" width={50} height={50} aria-hidden="true">
      <defs>
        <radialGradient id={fillId} cx="50%" cy="38%" r="65%">
          <stop offset="0%" stopColor="#F3E2B3" />
          <stop offset="60%" stopColor={GOLD} />
          <stop offset="100%" stopColor="#A9822F" />
        </radialGradient>
      </defs>
      {SEAL_RAYS.map(({ key, cx, cy }) => (
        <circle key={key} cx={cx} cy={cy} r={4.2} fill={`url(#${fillId})`} />
      ))}
      <circle cx="32" cy="32" r="24" fill={`url(#${fillId})`} />
      <circle
        cx="32"
        cy="32"
        r="18.5"
        fill="none"
        stroke="#fff"
        strokeOpacity="0.7"
        strokeWidth="1"
      />
      <path
        d="M24.5 32.5l5 5 10-11"
        fill="none"
        stroke="#fff"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const orgNameSize = (name) => {
  if (name.length > 48) return 12;
  if (name.length > 32) return 14;
  return 16;
};

const Certificate = ({
  organisationName,
  countryLabel,
  domain,
  logoSrc,
  t,
  language,
}) => {
  const hasName = Boolean(organisationName);
  const issuedOn = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language, { dateStyle: "medium" }).format(
        new Date(),
      );
    } catch {
      return new Date().toDateString();
    }
  }, [language]);

  return (
    <div
      className="relative overflow-hidden bg-white"
      style={{
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        fontFamily: "Inter, system-ui, sans-serif",
        boxShadow:
          "0 18px 40px -18px rgba(16,24,40,0.35), 0 2px 6px rgba(16,24,40,0.06)",
      }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 90% at 50% 0%, color-mix(in srgb, var(--cf-primary-color) 5%, white) 0%, #fff 55%)",
        }}
      />

      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0"
        style={{ height: 10, background: PRIMARY }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0"
        style={{ top: 10, height: 3, background: GOLD }}
      />

      <div
        aria-hidden="true"
        className="absolute"
        style={{
          inset: "26px 16px 16px 16px",
          border:
            "1px solid color-mix(in srgb, var(--cf-primary-color) 22%, white)",
        }}
      />
      <Corner position="tl" />
      <Corner position="tr" />
      <Corner position="bl" />
      <Corner position="br" />

      <div
        className="relative flex h-full flex-col items-center px-14 text-center"
        style={{ paddingTop: 36 }}
      >
        <Live value={logoSrc} className="!px-0">
          <span
            className={cn(
              "flex items-center justify-center",
              !logoSrc && "overflow-hidden",
            )}
            style={{
              height: 46,
              minWidth: 46,
              maxWidth: 170,
              borderRadius: logoSrc ? 0 : 10,
              padding: 0,
              background: logoSrc
                ? "transparent"
                : "color-mix(in srgb, var(--cf-primary-color) 10%, white)",
            }}
          >
            {logoSrc ? (
              <img
                src={logoSrc}
                alt=""
                style={{
                  height: 36,
                  width: "auto",
                  maxWidth: 154,
                  objectFit: "contain",
                }}
              />
            ) : (
              <ImageIcon
                aria-hidden="true"
                style={{ width: 20, height: 20, color: PRIMARY }}
                strokeWidth={1.6}
              />
            )}
          </span>
        </Live>

        <Live value={organisationName} className="mt-2.5 max-w-full">
          <span
            className="block max-w-[430px] truncate"
            style={{
              fontFamily: SERIF,
              fontSize: orgNameSize(organisationName),
              fontWeight: 600,
              letterSpacing: "0.01em",
              color: hasName ? INK : MUTED,
            }}
          >
            {hasName ? organisationName : t("onboarding:preview_org_fallback")}
          </span>
        </Live>

        <p
          className="mt-[14px]"
          style={{
            fontFamily: SERIF,
            fontSize: 34,
            lineHeight: 1,
            fontWeight: 700,
            letterSpacing: "0.12em",
            color: PRIMARY,
          }}
        >
          {t("onboarding:certificate_title")}
        </p>
        <p
          className="mt-1.5"
          style={{
            fontFamily: SERIF,
            fontSize: 10,
            letterSpacing: "0.38em",
            color: PRIMARY,
            textTransform: "uppercase",
          }}
        >
          {t("onboarding:certificate_subtitle")}
        </p>

        <div aria-hidden="true" className="mt-[14px] flex items-center gap-2">
          <span
            style={{
              width: 70,
              height: 1,
              background: `linear-gradient(90deg, transparent, ${GOLD})`,
            }}
          />
          <span
            style={{
              width: 6,
              height: 6,
              background: GOLD,
              transform: "rotate(45deg)",
            }}
          />
          <span
            style={{
              width: 70,
              height: 1,
              background: `linear-gradient(90deg, ${GOLD}, transparent)`,
            }}
          />
        </div>

        <p
          className="mt-[14px]"
          style={{ fontSize: 10, color: "#667085", letterSpacing: "0.02em" }}
        >
          {t("onboarding:certificate_certify")}
        </p>
        <p
          className="mt-1"
          style={{
            fontFamily: SERIF,
            fontStyle: "italic",
            fontSize: 22,
            color: INK,
          }}
        >
          {t("onboarding:certificate_recipient")}
        </p>
        <span
          aria-hidden="true"
          style={{ width: 210, height: 1, marginTop: 4, background: "#D0D5DD" }}
        />
        <p
          className="mt-2 max-w-[360px]"
          style={{ fontSize: 9.5, lineHeight: 1.5, color: "#667085" }}
        >
          {t("onboarding:certificate_body")}
        </p>

        <div
          className="absolute inset-x-0 flex items-end justify-between px-[60px]"
          style={{ bottom: 48 }}
        >
          <div className="min-w-0 text-left" style={{ width: 180 }}>
            <Live value={countryLabel} className="!px-0 block">
              <span
                className="block truncate"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: countryLabel ? INK : MUTED,
                }}
              >
                {countryLabel || t("onboarding:preview_country_fallback")}
              </span>
            </Live>
            <Live value={domain} className="!px-0 block">
              <span
                className="mt-0.5 block truncate"
                style={{ fontSize: 9.5, color: domain ? PRIMARY : MUTED }}
              >
                {domain || t("onboarding:preview_domain_fallback")}
              </span>
            </Live>
            <span
              className="mt-1.5 block"
              style={{ fontSize: 8.5, color: MUTED }}
            >
              {t("onboarding:certificate_issued", { date: issuedOn })}
            </span>
          </div>

          <Seal />

          <div className="flex flex-col items-end" style={{ width: 180 }}>
            <span
              style={{
                padding: 3,
                background: "#fff",
                boxShadow: "0 0 0 1px #EAECF0",
                borderRadius: 4,
              }}
            >
              <FakeQr />
            </span>
            <span
              className="mt-1"
              style={{ fontSize: 8, color: MUTED, letterSpacing: "0.04em" }}
            >
              {t("onboarding:certificate_scan")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const LiveCertificatePreview = ({
  organisationName,
  country,
  domain,
  logoSrc,
}) => {
  const { t, i18n } = useTranslation(["onboarding"]);
  const language = i18n?.language || "en";
  const [frameRef, scale] = useCanvasScale();

  const countryLabel = useMemo(
    () => findCountryOption(country, language)?.label || "",
    [country, language],
  );

  return (
    <section
      id={automationTestIds.onboardingCertificatePreview}
      aria-labelledby={`${automationTestIds.onboardingCertificatePreview}-title`}
    >
      <h2
        id={`${automationTestIds.onboardingCertificatePreview}-title`}
        className="text-base font-semibold text-cfHeadingText"
      >
        {t("onboarding:preview_title")}
      </h2>
      <p className="mt-1 text-sm text-cfGlobalText">
        {t("onboarding:preview_subtitle")}
      </p>

      <div className="mt-[20px] rounded-xl bg-cfSearchContainer p-[12px] sm:p-[20px]">
        <div ref={frameRef} className="w-full">
          <div
            role="img"
            aria-label={t("onboarding:certificate_aria", {
              name: organisationName || t("onboarding:preview_org_fallback"),
            })}
            style={{ height: CANVAS_HEIGHT * scale, position: "relative" }}
          >
            <div
              style={{
                width: CANVAS_WIDTH,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            >
              <Certificate
                organisationName={(organisationName || "").trim()}
                countryLabel={countryLabel}
                domain={domain}
                logoSrc={logoSrc}
                t={t}
                language={language}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveCertificatePreview;
